import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Button, Skeleton, Stack, styled, Typography } from '@mui/material';

import { useBreadcrumbs } from 'Breadcrumb';
import { AvatarConsts } from 'Common/UserAvatar/Avatar/Avatar.consts';
import { selectCoursesPaginationSettings } from 'Config/configSlice';
import { useAppDispatch, useAppSelector } from 'Store';
import { Spacing } from 'Themes/spacing';
import {
  setCourseViewMode,
  useSelectCoursePreferences,
  useSelectCurrentUserInfo,
  useSelectHasUserInfo,
  useSelectIsGetCurrentUserInfoLoading,
  useSelectIsStartBannerHidden,
  useSelectUserDisplayName
} from 'User/data';
import type { UserDisplayModel } from 'User/userDisplayModel';

import { BasicFullscreenProvider } from '../../components/fullscreen-desktop-viewer/basic-fullscreen-context';
import { BasicFullscreenViewer } from '../../components/fullscreen-desktop-viewer/basic-fullscreen-viewer';
import { CourseCard, SimpleCourseCard } from '../../components/shared';
import type { CourseModel, GetCoursesParams } from '../../data';
import {
  getCourses,
  getCourseTags,
  resetCoursesError,
  setCoursesLoaded,
  useSelectAreCoursesLoading,
  useSelectAreTagsLoading,
  useSelectAvailableTagsWithTranslations,
  useSelectCourses,
  useSelectCoursesPagination,
  useSelectIsCoursesLoaded
} from '../../data';
import { useJoinCoursesListGroup } from '../../data/use-join-courses-list-group';
import {
  decodeTagArray,
  QUERY_PARAM_EXCLUDE_TAGS,
  QUERY_PARAM_INCLUDE_TAGS,
  QUERY_PARAM_SEARCH
} from '../../hooks/urlSyncUtils';

import { CourseListViewActions } from './components/course-list-view-actions';
import { CoursesEmpty, DevConfigPanel, StartBanner } from './components';
import { GridWithRestrictedSize } from './courses-page.styles';
import type { CourseListViewMode } from './CoursesListView.consts';
import { CoursesListViewConsts } from './CoursesListView.consts';
import { useCoursesLoader, useCoursesSearch } from './hooks';

import type { BreadcrumbItem } from 'Breadcrumb';

const CoursesWrapper = styled('div')`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

export const breadcrumbs: BreadcrumbItem[] = [{ translateKey: 'coursesPage.courses', to: '/' }];

export const CoursesPage = () => {
  const { t } = useTranslation();

  return (
    <BasicFullscreenProvider>
      <CoursesPageContent />
      <BasicFullscreenViewer />
    </BasicFullscreenProvider>
  );
};

const CoursesPageContent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const paginationSettings = useAppSelector(selectCoursesPaginationSettings);

  useJoinCoursesListGroup();
  useBreadcrumbs(breadcrumbs);

  const areCoursesLoading = useSelectAreCoursesLoading();
  const coursePreferences = useSelectCoursePreferences();
  const courseListViewMode = coursePreferences?.viewMode ?? CoursesListViewConsts.defaultViewMode;
  const courses = useSelectCourses();
  const availableTags = useSelectAvailableTagsWithTranslations();
  const areTagsLoading = useSelectAreTagsLoading();

  const currentUserInfo = useSelectCurrentUserInfo();
  const currentUserInfoDisplayName = useSelectUserDisplayName() ?? AvatarConsts.fallbackUserName;
  const isStartBannerHidden = useSelectIsStartBannerHidden();
  const isUserInfoLoading = useSelectIsGetCurrentUserInfoLoading();
  const hasUserInfo = useSelectHasUserInfo();
  const isCoursesLoaded = useSelectIsCoursesLoaded();
  const searchText = (searchParams.get(QUERY_PARAM_SEARCH) ?? '').trim();
  const includeTags = decodeTagArray(searchParams.get(QUERY_PARAM_INCLUDE_TAGS));
  const excludeTags = decodeTagArray(searchParams.get(QUERY_PARAM_EXCLUDE_TAGS));
  const pagination = useSelectCoursesPagination();

  const { loadError } = useCoursesLoader();
  const { isSearchInProgress, hasActiveSearch } = useCoursesSearch();

  const currentUserModel: UserDisplayModel = {
    displayName: currentUserInfoDisplayName,
    avatarColor: currentUserInfo.avatarColor,
    avatar: currentUserInfo.avatar,
    customAvatar: null
  };

  const handleViewModeChange = (newViewMode: CourseListViewMode) => {
    dispatch(setCourseViewMode(newViewMode));
  };

  const handleLoadMore = () => {
    const nextPage = (pagination?.currentPage ?? 1) + 1;
    const searchParams: GetCoursesParams = {
      pageSize: paginationSettings.pageSize,
      page: nextPage,
      searchText: searchText?.trim(),
      includeTags: includeTags.length > 0 ? includeTags : undefined,
      excludeTags: excludeTags.length > 0 ? excludeTags : undefined
    };

    if (!searchParams.searchText) {
      delete searchParams.searchText;
    }

    dispatch(getCourses(searchParams));
  };

  useEffect(() => {
    const tagsArray = availableTags ?? [];
    if (tagsArray.length === 0 && !areTagsLoading) {
      dispatch(getCourseTags());
    }
  }, [dispatch, availableTags, areTagsLoading]);

  useEffect(() => {
    if (!areCoursesLoading && !isCoursesLoaded && !isSearchInProgress && !hasActiveSearch) {
      dispatch(getCourses({ pageSize: paginationSettings.pageSize, page: 1 }));
    }
  }, [
    areCoursesLoading,
    isCoursesLoaded,
    isSearchInProgress,
    hasActiveSearch,
    paginationSettings.pageSize
  ]);

  const regularCourses = courses.filter(course => !course.isTest);
  const ongoingCourses = regularCourses.filter(course => course.flags?.isInSession);
  const shouldSkipHighlighted = ongoingCourses.some(course => !course.hasFreshStart);

  const hasMoreCourses = pagination?.hasNextPage ?? false;

  const testCoursesCount = courses.filter(course => course.isTest).length;
  const totalRegularCourses = (pagination?.totalItems ?? 0) - testCoursesCount;

  const courseCards = regularCourses.map((course: CourseModel) => {
    const isOngoing = course.flags?.isInSession;

    if (courseListViewMode === CoursesListViewConsts.viewMode.simple && !course.myDesktop) {
      return <SimpleCourseCard key={course.id} course={course} />;
    }

    return (
      <CourseCard
        key={course.id}
        course={course}
        user={currentUserModel}
        isOngoing={isOngoing}
        shouldSkipHighlighted={shouldSkipHighlighted}
      />
    );
  });

  const isAnyCourse = regularCourses.length > 0;

  const shouldShowStartBanner = hasUserInfo && !isStartBannerHidden && !isUserInfoLoading;
  const isLoading = areCoursesLoading || isSearchInProgress;

  const handleRetry = () => {
    dispatch(resetCoursesError());
    dispatch(setCoursesLoaded(false));
    const retryParams: GetCoursesParams = {
      pageSize: paginationSettings.pageSize,
      page: 1,
      searchText: searchText?.trim() || undefined,
      includeTags: includeTags.length > 0 ? includeTags : undefined,
      excludeTags: excludeTags.length > 0 ? excludeTags : undefined
    };
    dispatch(getCourses(retryParams));
  };

  if (isLoading) {
    return (
      <>
        {shouldShowStartBanner && <StartBanner isHighlighted={true} />}
        <Stack spacing={Spacing.small} sx={{ marginTop: '1rem' }}>
          <Skeleton variant="rounded" animation="wave" width="100%" height={50} />
          <Skeleton variant="rounded" animation="wave" width="100%" height={50} />
          <Skeleton variant="rounded" animation="wave" width="100%" height={50} />
        </Stack>
      </>
    );
  }

  if (loadError && !isCoursesLoaded) {
    return (
      <GridWithRestrictedSize>
        <Stack spacing={2} alignItems="center" sx={{ mt: 4 }}>
          <Typography variant="h6" color="error">
            {t('coursesPage.loadError', 'Failed to load courses')}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: 'center', maxWidth: 500 }}
          >
            {loadError}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={handleRetry}>
              {t('common.retry', 'Retry')}
            </Button>
            <Button variant="outlined" onClick={() => window.location.reload()}>
              {t('common.refresh', 'Refresh Page')}
            </Button>
          </Stack>
        </Stack>
        <DevConfigPanel />
      </GridWithRestrictedSize>
    );
  }

  return (
    <>
      {shouldShowStartBanner && <StartBanner isHighlighted={!isAnyCourse} />}

      {isAnyCourse ? (
        <GridWithRestrictedSize>
          <Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h2>{t('coursesPage.courses')}</h2>
              </div>

              <CourseListViewActions
                viewMode={courseListViewMode}
                onViewModeChange={handleViewModeChange}
              />
            </Stack>

            <CoursesWrapper>{courseCards}</CoursesWrapper>

            {totalRegularCourses > 0 && (
              <Stack alignItems="center" sx={{ mt: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {t('coursesPage.totalCourses', 'Total courses: {{count}}', {
                    count: totalRegularCourses
                  })}
                </Typography>
              </Stack>
            )}

            {hasMoreCourses && (
              <Stack alignItems="center" sx={{ mt: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleLoadMore}
                  disabled={areCoursesLoading}
                  sx={{ minWidth: 200 }}
                >
                  {areCoursesLoading
                    ? t('common.loading', 'Loading...')
                    : t('coursesPage.loadMore', 'Load More')}
                </Button>
              </Stack>
            )}
          </Stack>
        </GridWithRestrictedSize>
      ) : (
        <CoursesEmpty />
      )}

      <DevConfigPanel />
    </>
  );
};

CoursesPageContent.displayName = 'CoursesPage';
